import { Action, ActionReducer } from '@ngrx/store';
import { MemberProfileKey } from '@shared/local-storage.key';
import { AuthActions } from './auth-action.types';
import { MemberProfileActions } from '@core/entity/member-profile/state/member-profile-action.types';

/**
 * Keeps template-02's session keys (`user_data`, `user_token`) in step with qply's store.
 *
 * qply's login only writes `memberProfile` / `authToken` (see the effects), but ~30 ported
 * files still read `user_data` and `user_token` -- topbar, wallet bar, transaction socket,
 * socket.io-service, live chat. Without them every reload while logged in crashes on
 * `null.id` / `null.currency` and leaves the page half logged in (AIO-82553).
 *
 * A meta-reducer rather than an effect on purpose: ngrx notifies state subscribers before it
 * runs effects, and `app.component` reacts to IsLoggedIn synchronously (socket join, transaction
 * socket). Writing here lands the keys before any of that code runs.
 */
export function legacySessionKeys(reducer: ActionReducer<any>): ActionReducer<any> {
  return (state, action) => {
    if (typeof window !== 'undefined') {
      try {
        syncLegacyKeys(action);
      } catch { /* storage unavailable -- never block the reducer */ }
    }
    return reducer(state, action);
  };
}

function syncLegacyKeys(action: Action) {
  switch (action.type) {
    case MemberProfileActions.SetMemberProfile.type:
      localStorage.setItem('user_data', JSON.stringify(withoutType(action)));
      break;

    case AuthActions.Login.type:
      localStorage.setItem('user_token', JSON.stringify(withoutType(action)));
      // Reload / URL-param paths dispatch Login before (or without) SetMemberProfile, so
      // backfill from the stored profile; a fresh form login sets the profile first.
      if (!localStorage.getItem('user_data')) {
        const profile = localStorage.getItem(MemberProfileKey);
        if (profile) localStorage.setItem('user_data', JSON.stringify(withoutType(JSON.parse(profile))));
      }
      break;

    case AuthActions.Logout.type:
      localStorage.removeItem('user_token');
      localStorage.removeItem('user_data');
      // AIO-82318: the pending two-step deposit (merchant bank details) belongs to this member.
      localStorage.removeItem('TwostepDeposit');
      break;

    case MemberProfileActions.UnsetMemberProfile.type:
      localStorage.removeItem('user_data');
      break;
  }
}

function withoutType<T extends { type?: string }>(value: T): Omit<T, 'type'> {
  const { type, ...rest } = value;
  return rest;
}
